import { ArrowLeft, Search } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBillsWithQr, updateQrStatus } from '../../../api/feedback.api';
import styles from '../../../assets/scss/pages/FeedbackPage.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatCurrency, formatPhoneNumber } from '../../../utils/formatters';

const PAGE_SIZE = 50;

function fmtDateTime(str) {
    if (!str) return '—';
    const d = new Date(str);
    return d.toLocaleDateString('vi-VN') + ' ' + d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
}

export default function FeedbackQr() {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canUpdateQr = hasPermission(PERMISSIONS.FEEDBACK.UPDATE_QR_STATUS);

    const [bills, setBills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [toastMsg, setToastMsg] = useState(null);
    const fetchIdRef = useRef(0);

    const showToast = useCallback((type, text, duration = 1500) => {
        setToastMsg({ type, text });
        setTimeout(() => setToastMsg(null), duration);
    }, []);

    const fetchBills = useCallback(
        async (p = page) => {
            if (!canUpdateQr) {
                setLoading(false);
                return;
            }
            const fetchId = ++fetchIdRef.current;
            setLoading(true);
            try {
                const { data } = await getBillsWithQr({
                    limit: PAGE_SIZE,
                    page: p,
                    isFeedback: filter === 'pending' ? '0' : '1',
                });
                if (fetchId !== fetchIdRef.current) return;
                // API trả { data: bills[], infopages: { total, page, limit, totalPages } }
                const bills = data.data || data.bills || [];
                const info = data.infopages || data.pagination || {};
                const list = bills.filter((b) => b.customer?.name && b.customer.name !== 'Khách lẻ');
                setBills(list);
                setTotalItems(info.total || list.length);
                setTotalPages(info.totalPages || Math.max(1, Math.ceil((info.total || list.length) / PAGE_SIZE)));
            } catch {
                if (fetchId !== fetchIdRef.current) return;
                setBills([]);
            } finally {
                if (fetchId === fetchIdRef.current) setLoading(false);
            }
        },
        [canUpdateQr, filter, page],
    );

    useEffect(() => {
        fetchBills(page);
    }, [fetchBills, page]);

    const handleFilterChange = (f) => {
        setFilter(f);
        setPage(1);
    };

    const [markingId, setMarkingId] = useState(null);

    const handleMarkSent = async (billId) => {
        if (!canUpdateQr) return;
        setMarkingId(billId);
        try {
            await updateQrStatus(billId);
            setBills((prev) => prev.filter((b) => b.id !== billId));
            showToast('success', 'Đã đánh dấu gửi QR');
        } catch {
            showToast('error', 'Thất bại', 2500);
        } finally {
            setMarkingId(null);
        }
    };

    const normalize = (str) =>
        (str || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/đ/g, 'd')
            .replace(/Đ/g, 'D')
            .replace(/\s+/g, '')
            .toLowerCase();

    const filtered = bills.filter((b) => {
        if (!search.trim()) return true;
        const q = normalize(search);
        return normalize(b.customer?.name).includes(q) || normalize(b.customer?.phone).includes(q);
    });

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div className={styles.headerLeft} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <button className={styles.backBtn} onClick={() => navigate(-1)}>
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className={styles.title} style={{ fontSize: 20, marginBottom: 2 }}>
                            QR đánh giá
                        </h1>
                        <p className={styles.subtitle}>QR gửi cho khách sau khi hoàn thành đơn</p>
                    </div>
                </div>
            </div>

            <div className={styles.filters}>
                <button
                    className={`${styles.filterBtn}${filter === 'pending' ? ` ${styles.filterBtnActive}` : ''}`}
                    onClick={() => handleFilterChange('pending')}
                >
                    Chưa gửi
                </button>
                <button
                    className={`${styles.filterBtn}${filter === 'sent' ? ` ${styles.filterBtnActive}` : ''}`}
                    onClick={() => handleFilterChange('sent')}
                >
                    Đã gửi
                </button>
                <div className={styles.searchWrap}>
                    <Search className={styles.searchIcon} />
                    <input
                        className={styles.searchInput}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Tìm tên, SĐT..."
                    />
                </div>
                <span className={styles.filterCount}>{totalItems} hoá đơn</span>
            </div>

            <div className={styles.tableWrapper}>
                {loading ? (
                    <div className={styles.loading}>
                        <div className={styles.spinner} /> Đang tải...
                    </div>
                ) : filtered.length === 0 ? (
                    <div className={styles.empty}>Không có hoá đơn nào</div>
                ) : (
                    <>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th style={{ width: 45, textAlign: 'center' }}>STT</th>
                                    <th>Khách hàng</th>
                                    <th>SĐT</th>
                                    <th>Tổng tiền</th>
                                    <th>Thời gian</th>
                                    <th style={{ width: 100 }}>Trạng thái</th>
                                    {filter === 'pending' && canUpdateQr && <th style={{ width: 140 }}>Thao tác</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((bill, idx) => (
                                    <tr key={bill.id} style={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/admin/feedback/qr/${bill.id}`)}>
                                        <td style={{ textAlign: 'center', color: '#6B7280' }}>
                                            {(page - 1) * PAGE_SIZE + idx + 1}
                                        </td>
                                        <td style={{ fontWeight: 500 }}>{bill.customer?.name}</td>
                                        <td style={{ color: '#6B7280' }}>
                                            {bill.customer?.phone ? formatPhoneNumber(bill.customer.phone) : '—'}
                                        </td>
                                        <td>{formatCurrency(bill.total || 0)}</td>
                                        <td style={{ color: '#6B7280', fontSize: 13 }}>
                                            {fmtDateTime(bill.checkoutTime)}
                                        </td>
                                        <td>
                                            <span
                                                className={`${styles.qrBadge} ${Number(bill.isFeedback) === 1 ? styles.qrBadgeSent : styles.qrBadgePending}`}
                                            >
                                                {Number(bill.isFeedback) === 1 ? 'Đã gửi' : 'Chưa gửi'}
                                            </span>
                                        </td>
                                        {filter === 'pending' && canUpdateQr && (
                                            <td>
                                                <button
                                                    className={styles.qrActionBtn}
                                                    onClick={(e) => { e.stopPropagation(); handleMarkSent(bill.id); }}
                                                    disabled={markingId === bill.id}
                                                >
                                                    {markingId === bill.id ? 'Đang tải...' : 'Hoàn thành'}
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {totalPages > 1 && (
                            <div className={styles.pagination}>
                                <span className={styles.paginationInfo}>
                                    {`${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, totalItems)} / ${totalItems}`}
                                </span>
                                <div className={styles.paginationControls}>
                                    <button
                                        className={styles.pageBtn}
                                        disabled={page <= 1}
                                        onClick={() => setPage(page - 1)}
                                    >
                                        {'‹'}
                                    </button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                                        .filter((n) => n === 1 || n === totalPages || Math.abs(n - page) <= 2)
                                        .reduce((acc, n, i, arr) => {
                                            if (i > 0 && n - arr[i - 1] > 1) acc.push('dots-' + i);
                                            acc.push(n);
                                            return acc;
                                        }, [])
                                        .map((n) =>
                                            typeof n === 'string' ? (
                                                <span key={n} className={styles.pageDots}>
                                                    ...
                                                </span>
                                            ) : (
                                                <button
                                                    key={n}
                                                    className={`${styles.pageBtn}${n === page ? ` ${styles.pageBtnActive}` : ''}`}
                                                    onClick={() => setPage(n)}
                                                >
                                                    {n}
                                                </button>
                                            ),
                                        )}
                                    <button
                                        className={styles.pageBtn}
                                        disabled={page >= totalPages}
                                        onClick={() => setPage(page + 1)}
                                    >
                                        {'›'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {toastMsg && (
                <div className={styles.toastOverlay}>
                    <div className={styles.toastModal}>
                        <div
                            className={styles.toastIcon}
                            style={toastMsg.type === 'error' ? { background: '#EF4444' } : undefined}
                        >
                            {toastMsg.type === 'success' ? '✓' : '✕'}
                        </div>
                        <span className={styles.toastText}>{toastMsg.text}</span>
                    </div>
                </div>
            )}
        </div>
    );
}
